import { cva, type VariantProps } from 'class-variance-authority';

export const buttonVariants = cva(
    'inline-flex items-center text-[length:var(--button-font-size)] justify-center gap-2 px-4 whitespace-nowrap rounded-lg font-sans transition-colors disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive',
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active disabled:bg-input disabled:text-muted-foreground',
                primary:
                    'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active disabled:bg-input disabled:text-muted-foreground',
                secondary:
                    'bg-background border border-foreground text-foreground hover:bg-muted active:bg-outline-pressed disabled:border-input disabled:text-muted-foreground',
                tertiary:
                    'bg-transparent px-0 text-foreground underline underline-offset-[6px] decoration-1 hover:text-tertiary-hover disabled:text-muted-foreground active:text-tertiary-active active:decoration-[1.5px]',
                purchase:
                    'bg-snk-cart-green text-white hover:bg-snk-cart-green-hover active:bg-snk-cart-green-active disabled:bg-input disabled:text-muted-foreground',
                'purchase-priced':
                    'bg-snk-cart-green text-white gap-4 hover:bg-snk-cart-green-hover active:bg-snk-cart-green-active disabled:bg-input disabled:text-muted-foreground',
                matches:
                    'bg-background border border-snk-matches-pink text-foreground hover:bg-snk-matches-pink hover:text-white hover:border-snk-matches-pink active:bg-snk-matches-pink/90 active:text-white aria-pressed:bg-snk-matches-pink aria-pressed:text-white aria-pressed:border-snk-matches-pink disabled:border-input disabled:text-muted-foreground',
                shadow: 'bg-white text-foreground rounded-full shadow-[0_0_10px_0_rgba(0,0,0,0.25)]',
                link: 'bg-transparent px-0 text-primary normal-case font-light tracking-[0.01em] hover:font-normal hover:underline hover:opacity-90 active:font-normal active:underline active:opacity-100 underline-offset-[6px] decoration-1 disabled:cursor-not-allowed disabled:text-muted-foreground aria-disabled:cursor-not-allowed aria-disabled:text-muted-foreground aria-disabled:pointer-events-none',
                'link-caps':
                    'bg-transparent px-0 text-primary uppercase font-normal tracking-[0.12em] hover:font-medium hover:underline hover:opacity-90 active:font-medium active:underline active:opacity-100 underline-offset-[6px] decoration-1 disabled:cursor-not-allowed disabled:text-muted-foreground aria-disabled:cursor-not-allowed aria-disabled:text-muted-foreground aria-disabled:pointer-events-none',
            },
            size: {
                default: "h-[38px] py-2 tracking-btn uppercase [&_svg:not([class*='size-'])]:size-4",
                sm: "h-8 py-1 tracking-btn uppercase [&_svg:not([class*='size-'])]:size-4",
                lg: "h-12 py-3 tracking-btn uppercase [&_svg:not([class*='size-'])]:size-4",
                icon: '[&_svg]:size-5 h-10 w-10 p-0',
                'icon-sm': '[&_svg]:size-4 h-8 w-8 p-0',
                'icon-lg': '[&_svg]:size-6 h-12 w-12 p-0',
            },
            /** Only affects `link` and `link-caps`; removes hover/active underline when false. */
            linkHoverUnderline: {
                true: '',
                false: '',
            },
        },
        compoundVariants: [
            {
                variant: 'tertiary',
                size: 'sm',
                class: 'normal-case tracking-[0.01em] text-base leading-5 h-auto py-0',
            },
            {
                variant: 'tertiary',
                size: ['default', 'lg'],
                class: 'h-auto py-0 leading-4 tracking-[2.1px]',
            },
            {
                variant: 'purchase-priced',
                size: ['default', 'sm', 'lg'],
                class: 'py-0',
            },
            {
                variant: 'link',
                size: ['default', 'sm', 'lg'],
                class: 'h-auto py-0 normal-case text-base tracking-normal cursor-pointer disabled:cursor-not-allowed',
            },
            {
                variant: 'link-caps',
                size: ['default', 'sm', 'lg'],
                class: 'h-auto py-0 text-base uppercase cursor-pointer disabled:cursor-not-allowed',
            },
            {
                variant: 'link',
                linkHoverUnderline: false,
                class: 'hover:no-underline active:no-underline',
            },
            {
                variant: 'link-caps',
                linkHoverUnderline: false,
                class: 'hover:no-underline active:no-underline',
            },
        ],
        defaultVariants: {
            variant: 'default',
            size: 'default',
            linkHoverUnderline: true,
        },
    }
);

export type ButtonVariantsProps = VariantProps<typeof buttonVariants>;
